import type { JSX } from 'preact';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'preact/hooks';
import { createSkiffBoat, SKIFF_BOAT_ID, type BoatDocument } from '../../sim/boat/index.ts';
import type { BotProfileDocument } from '../../sim/botProfile/types.ts';
import { formatElapsed, type HighScore } from '../../sim/highScores/index.ts';
import {
  CATALOG_COMPETITOR_MAX,
  MIN_COMPETITORS,
  type MatchMovement,
} from '../../sim/match/index.ts';
import type { SeatController } from '../../sim/bot/seats.ts';
import { boatCollisionInstalled } from '../../sim/modules/host.ts';
import { setPanel } from '../panels.ts';
import { isTypingInField } from '../overlayTyping.ts';
import { dealCompetitorNames } from '../play/competitorNames.ts';
import { BoatSideArt } from '../boats/sideArt.tsx';
import {
  BoatSelect,
  middleBoatId,
  type BoatSelectChoice,
} from './boatSelect.tsx';
import {
  focusStartNav,
  focusedStartNavId,
  moveStartNav,
  readStartNav,
  startNavDir,
  startNavElement,
  startNavProps,
} from './keyboard.ts';
import './mapSelect.css';

export type MapSelectEntry = {
  id: string;
  label: string;
  checkpointCount: number;
  /** Authored lap count. 1 omits the lap phrase. */
  laps: number;
  hasGoal: boolean;
  topScores: readonly HighScore[];
};

export type StartBoatEntry = {
  id: string;
  label: string;
  boat: BoatDocument;
};

/** One line under a saved bot in the Bots pane. */
function botSummary(profile: BotProfileDocument): string {
  const { knobs } = profile;
  const base = profile.basedOn.charAt(0).toUpperCase() + profile.basedOn.slice(1);
  return `From ${base} · foresight ${knobs.depth} · slips ${knobs.temperature.toFixed(2)} · aim ${knobs.aimErrorM.toFixed(1)} m`;
}

/** A saved bot profile, for the Bots pane and the Hot Seat Helm row. */
export type StartBotEntry = {
  id: string;
  label: string;
  profile: BotProfileDocument;
};

export type RaceBoatPick = {
  id: string;
  boat: BoatDocument;
  /** Dealt or edited racer name for this seat. */
  name: string;
  /** Human or bot. Time Trial is always human. */
  controller: SeatController;
};

export type MapSelectProps = {
  maps: readonly MapSelectEntry[];
  boats: readonly StartBoatEntry[];
  bots?: readonly StartBotEntry[];
  onSelect: (
    id: string,
    competitorCount: number,
    boats: readonly RaceBoatPick[],
    movement: MatchMovement,
    boatCollisions: boolean,
  ) => void;
  onEdit: (id: string) => void;
  onBlankEditor: () => void;
  onEditBoat: (id: string) => void;
  onNewBoat: () => void;
  /** Opens the bot workshop on a saved profile. */
  onEditBot?: (id: string) => void;
  /** Opens the bot workshop on a new profile. */
  onNewBot?: () => void;
  /** Pane open on mount, e.g. Bots again after the bot workshop closes. */
  initialView?: 'bots';
  onReplay: (id: string, score: HighScore) => void;
};

export type MapSelect = {
  destroy: () => void;
};

type StartView = 'idle' | 'race' | 'editor' | 'boats' | 'bots' | 'scores' | 'about';

/** Catalog race modes. Time Trial is solo; Hot Seat picks 1–4 boats. */
type RaceMode = 'timeTrial' | 'hotSeat';

const MENU: readonly { id: Exclude<StartView, 'idle'>; label: string }[] = [
  { id: 'race', label: 'Go Race' },
  { id: 'editor', label: 'Editor' },
  { id: 'boats', label: 'Boats' },
  { id: 'bots', label: 'Bots' },
  { id: 'scores', label: 'Top lists' },
  { id: 'about', label: 'About' },
];

const HOT_SEAT_BOAT_OPTIONS = Array.from(
  { length: CATALOG_COMPETITOR_MAX - MIN_COMPETITORS + 1 },
  (_, index) => MIN_COMPETITORS + index,
);

/** Hot Seat turn style. Sequential is the default; two boats are needed for the other. */
const MOVEMENT_OPTIONS: readonly { id: MatchMovement; label: string }[] = [
  { id: 'sequential', label: 'Sequential' },
  { id: 'simultaneous', label: 'Simultaneous' },
];

/** Boat-to-boat hits. On is the default; one boat has nobody to meet. */
const COLLISION_OPTIONS: readonly {
  id: 'on' | 'off';
  label: string;
  enabled: boolean;
}[] = [
  { id: 'on', label: 'On', enabled: true },
  { id: 'off', label: 'Off', enabled: false },
];

function describeMap(map: MapSelectEntry): string {
  if (!map.hasGoal) {
    return 'No goal set';
  }

  const count = map.checkpointCount;
  const laps = map.laps > 1 ? ` · ${map.laps} laps` : '';
  return `${count} checkpoint${count === 1 ? '' : 's'}${laps} · goal set`;
}

function ReplayPlayIcon(): JSX.Element {
  return (
    <svg
      class="map-select-item-replay-icon"
      viewBox="0 0 16 16"
      aria-hidden="true"
    >
      <path
        fill="currentColor"
        fill-rule="evenodd"
        d="M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0Zm-2 4.35v7.3L12.3 8 6 4.35Z"
      />
    </svg>
  );
}

function scoreBoatName(score: HighScore): string {
  const name = score.boat.name.trim();
  return name || score.boat.id;
}

function selectableBoats(boats: readonly StartBoatEntry[]): BoatSelectChoice[] {
  if (boats.length > 0) {
    return boats.map((entry) => ({ id: entry.id, boat: entry.boat }));
  }

  return [{ id: SKIFF_BOAT_ID, boat: createSkiffBoat() }];
}

const RACE_RULE_GROUPS = new Set(['boats', 'movement', 'collisions']);

/** After Enter locks a Hot Seat rule, focus the next rule or the map list. */
function focusAfterRaceRule(group: string, navId: string): string {
  if (group === 'boats') {
    const count = Number(navId.slice('boats-'.length));
    if (Number.isFinite(count) && count >= 2) {
      return 'movement-sequential';
    }

    return 'maps';
  }

  if (group === 'movement') {
    return boatCollisionInstalled() ? 'collisions-on' : 'maps';
  }

  return 'maps';
}

function selectedIdForPick(
  pick: RaceBoatPick | undefined,
  boats: readonly BoatSelectChoice[],
): string | null {
  if (!pick) {
    return null;
  }

  return boats.some((entry) => entry.id === pick.id) ? pick.id : null;
}

function MapTimes({
  map,
  onReplay,
}: {
  map: MapSelectEntry;
  onReplay: (id: string, score: HighScore) => void;
}): JSX.Element {
  return (
    <ol class="map-select-item-times" aria-label={`Top times for ${map.label}`}>
      {map.topScores.map((score, index) => {
        const time = formatElapsed(score.calculatedTimeMs);
        const name = scoreBoatName(score);
        const playable = score.replay !== null;

        return (
          <li
            key={`${map.id}-${score.recordedAt}-${score.calculatedTimeMs}-${index}`}
            class="map-select-item-time"
          >
            {playable ? (
              <button
                type="button"
                class="map-select-item-time-button"
                data-map-replay={map.id}
                aria-label={`Replay ${name} ${time}`}
                {...startNavProps('scores', `replay-${map.id}-${index}`, 'pane')}
                onClick={() => onReplay(map.id, score)}
              >
                <span class="map-select-item-time-value">
                  {name} · {time}
                </span>
                <ReplayPlayIcon />
              </button>
            ) : (
              `${name} · ${time}`
            )}
          </li>
        );
      })}
    </ol>
  );
}

function MapList({
  maps,
  mode,
  onSelect,
  onEdit,
}: {
  maps: readonly MapSelectEntry[];
  mode: 'race' | 'edit';
  onSelect: (id: string) => void;
  onEdit: (id: string) => void;
}): JSX.Element {
  if (maps.length === 0) {
    return (
      <p class="start-pane-empty">
        No maps found. Export one from the editor into the maps/ folder.
      </p>
    );
  }

  return (
    <ul class="start-list">
      {maps.map((map) => (
        <li key={map.id}>
          <button
            type="button"
            class="start-choice"
            data-map-id={mode === 'race' ? map.id : undefined}
            data-map-edit={mode === 'edit' ? map.id : undefined}
            aria-label={mode === 'edit' ? `Edit map ${map.label}` : undefined}
            {...(mode === 'race'
              ? startNavProps('maps', `map-${map.id}`, 'maps')
              : startNavProps('editor', `edit-${map.id}`, 'pane'))}
            onClick={() => {
              if (mode === 'race') {
                onSelect(map.id);
                return;
              }

              onEdit(map.id);
            }}
          >
            <span class="map-select-item-name">{map.label}</span>
            <span class="start-choice-summary">{describeMap(map)}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function BoatCountPicker({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (next: number) => void;
}): JSX.Element {
  return (
    <div class="start-boats" role="group" aria-label="Boats">
      <p class="start-pane-label">Boats</p>
      <div class="start-boats-options">
        {HOT_SEAT_BOAT_OPTIONS.map((count) => (
          <button
            key={count}
            type="button"
            class="start-boats-option"
            data-boats={count}
            aria-pressed={value === count}
            {...startNavProps('boats', `boats-${count}`, 'pane', 'horizontal')}
            onClick={() => onChange(count)}
          >
            {count}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Sequential plays each move as it is committed. Simultaneous has every player lock a hidden
 * move, then moves the whole field at once — the shape online play will take.
 */
function MovementPicker({
  value,
  onChange,
}: {
  value: MatchMovement | null;
  onChange: (next: MatchMovement) => void;
}): JSX.Element {
  return (
    <div class="start-boats start-movement" role="group" aria-label="Movement">
      <p class="start-pane-label">Movement</p>
      <div class="start-boats-options">
        {MOVEMENT_OPTIONS.map((option) => (
          <button
            key={option.id}
            type="button"
            class="start-boats-option"
            data-movement={option.id}
            aria-pressed={value !== null && value === option.id}
            {...startNavProps(
              'movement',
              `movement-${option.id}`,
              'pane',
              'horizontal',
            )}
            onClick={() => onChange(option.id)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Hits between hulls. Shore, rock, bounds, and shallow stay either way.
 * On is the race as it has always been; Off lets boats pass through each other.
 */
function CollisionsPicker({
  value,
  onChange,
}: {
  value: boolean | null;
  onChange: (next: boolean) => void;
}): JSX.Element {
  return (
    <div
      class="start-boats start-movement start-collisions"
      role="group"
      aria-label="Collisions between boats"
    >
      <p class="start-pane-label">Collisions</p>
      <div class="start-boats-options">
        {COLLISION_OPTIONS.map((option) => (
          <button
            key={option.id}
            type="button"
            class="start-boats-option"
            data-boat-collisions={option.id}
            aria-pressed={value !== null && value === option.enabled}
            {...startNavProps(
              'collisions',
              `collisions-${option.id}`,
              'pane',
              'horizontal',
            )}
            onClick={() => onChange(option.enabled)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function RacePane({
  maps,
  raceMode,
  hotSeatBoats,
  movement,
  boatCollisions,
  onRaceMode,
  onHotSeatBoats,
  onMovement,
  onBoatCollisions,
  onPickMap,
  onEdit,
}: {
  maps: readonly MapSelectEntry[];
  raceMode: RaceMode | null;
  hotSeatBoats: number | null;
  movement: MatchMovement | null;
  boatCollisions: boolean | null;
  onRaceMode: (mode: RaceMode | null) => void;
  onHotSeatBoats: (count: number) => void;
  onMovement: (next: MatchMovement) => void;
  onBoatCollisions: (next: boolean) => void;
  onPickMap: (id: string) => void;
  onEdit: (id: string) => void;
}): JSX.Element {
  const competitorCount =
    raceMode === 'timeTrial'
      ? 1
      : raceMode === 'hotSeat'
        ? hotSeatBoats
        : null;
  const showMaps = competitorCount !== null;

  return (
    <div
      class={`start-pane start-race-pane${showMaps ? ' is-ready-for-map' : ''}`}
      role="region"
      aria-label={showMaps ? 'Select a map' : 'Select a race mode'}
    >
      <div class="start-race-modes" role="group" aria-label="Race mode">
        <button
          type="button"
          class="start-choice start-race-mode"
          data-race-mode="timeTrial"
          aria-pressed={raceMode === 'timeTrial'}
          {...startNavProps('race-mode', 'timeTrial', 'pane')}
          onClick={() =>
            onRaceMode(raceMode === 'timeTrial' ? null : 'timeTrial')
          }
        >
          <span class="map-select-item-name">Time Trial</span>
        </button>
        <div class="start-race-hot-seat">
          <button
            type="button"
            class="start-choice start-race-mode"
            data-race-mode="hotSeat"
            aria-pressed={raceMode === 'hotSeat'}
            {...startNavProps('race-mode', 'hotSeat', 'pane')}
            onClick={() => onRaceMode(raceMode === 'hotSeat' ? null : 'hotSeat')}
          >
            <span class="map-select-item-name">Hot Seat</span>
          </button>
          {raceMode === 'hotSeat' ? (
            <BoatCountPicker value={hotSeatBoats} onChange={onHotSeatBoats} />
          ) : null}
          {raceMode === 'hotSeat' && (hotSeatBoats ?? 0) > 1 ? (
            <MovementPicker value={movement} onChange={onMovement} />
          ) : null}
          {raceMode === 'hotSeat' && (hotSeatBoats ?? 0) > 1 && boatCollisionInstalled() ? (
            <CollisionsPicker
              value={boatCollisions}
              onChange={onBoatCollisions}
            />
          ) : null}
        </div>
      </div>
      {showMaps && competitorCount !== null ? (
        <div class="start-race-maps">
          <p class="start-pane-label">Map</p>
          <MapList
            maps={maps}
            mode="race"
            onSelect={onPickMap}
            onEdit={onEdit}
          />
        </div>
      ) : null}
    </div>
  );
}

function StartPane({
  view,
  maps,
  boats,
  raceMode,
  hotSeatBoats,
  movement,
  boatCollisions,
  onRaceMode,
  onHotSeatBoats,
  onMovement,
  onBoatCollisions,
  onPickMap,
  onEdit,
  onBlankEditor,
  onEditBoat,
  onNewBoat,
  bots,
  onEditBot,
  onNewBot,
  onReplay,
}: {
  view: Exclude<StartView, 'idle'>;
  maps: readonly MapSelectEntry[];
  boats: readonly StartBoatEntry[];
  bots: readonly StartBotEntry[];
  onEditBot: (id: string) => void;
  onNewBot: () => void;
  raceMode: RaceMode | null;
  hotSeatBoats: number | null;
  movement: MatchMovement | null;
  boatCollisions: boolean | null;
  onRaceMode: (mode: RaceMode | null) => void;
  onHotSeatBoats: (count: number) => void;
  onMovement: (next: MatchMovement) => void;
  onBoatCollisions: (next: boolean) => void;
  onPickMap: (id: string) => void;
  onEdit: (id: string) => void;
  onBlankEditor: () => void;
  onEditBoat: (id: string) => void;
  onNewBoat: () => void;
  onReplay: (id: string, score: HighScore) => void;
}): JSX.Element {
  if (view === 'race') {
    return (
      <RacePane
        maps={maps}
        raceMode={raceMode}
        hotSeatBoats={hotSeatBoats}
        movement={movement}
        boatCollisions={boatCollisions}
        onRaceMode={onRaceMode}
        onHotSeatBoats={onHotSeatBoats}
        onMovement={onMovement}
        onBoatCollisions={onBoatCollisions}
        onPickMap={onPickMap}
        onEdit={onEdit}
      />
    );
  }

  if (view === 'editor') {
    return (
      <div class="start-pane" role="region" aria-label="Editor">
        <ul class="start-list">
          <li>
            <button
              type="button"
              class="start-choice"
              data-start-new-map
              {...startNavProps('editor', 'edit-new', 'pane')}
              onClick={onBlankEditor}
            >
              <span class="map-select-item-name">Start new map</span>
              <span class="start-choice-summary">Blank chart · unbound</span>
            </button>
          </li>
        </ul>
        <p class="start-pane-label">Existing maps</p>
        <MapList
          maps={maps}
          mode="edit"
          onSelect={() => {}}
          onEdit={onEdit}
        />
      </div>
    );
  }

  if (view === 'boats') {
    return (
      <div class="start-pane" role="region" aria-label="Boats">
        <ul class="start-list">
          <li>
            <button
              type="button"
              class="start-choice"
              data-start-new-boat
              {...startNavProps('crafts', 'craft-new', 'pane')}
              onClick={onNewBoat}
            >
              <span class="map-select-item-name">New boat</span>
              <span class="start-choice-summary">Blank hull · unbound</span>
            </button>
          </li>
        </ul>
        <p class="start-pane-label">Existing boats</p>
        {boats.length === 0 ? (
          <p class="start-pane-empty">No boats yet. Save one from the workshop into the boats/ folder.</p>
        ) : (
          <ul class="start-list">
            {boats.map((boat) => (
              <li key={boat.id}>
                <button
                  type="button"
                  class="start-choice"
                  data-boat-edit={boat.id}
                  aria-label={`Edit boat ${boat.label}`}
                  {...startNavProps('crafts', `craft-${boat.id}`, 'pane')}
                  onClick={() => onEditBoat(boat.id)}
                >
                  <BoatSideArt boat={boat.boat} />
                  <span class="map-select-item-name">{boat.label}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  if (view === 'bots') {
    return (
      <div class="start-pane" role="region" aria-label="Bots">
        <ul class="start-list">
          <li>
            <button
              type="button"
              class="start-choice"
              data-start-new-bot
              {...startNavProps('bots', 'bot-new', 'pane')}
              onClick={onNewBot}
            >
              <span class="map-select-item-name">New bot</span>
              <span class="start-choice-summary">Starts as Expert · unbound</span>
            </button>
          </li>
        </ul>
        <p class="start-pane-label">Saved bots</p>
        {bots.length === 0 ? (
          <p class="start-pane-empty">No bots yet. Save one from the workshop into the bots/ folder.</p>
        ) : (
          <ul class="start-list">
            {bots.map((bot) => (
              <li key={bot.id}>
                <button
                  type="button"
                  class="start-choice"
                  data-bot-edit={bot.id}
                  aria-label={`Edit bot ${bot.label}`}
                  {...startNavProps('bots', `bot-${bot.id}`, 'pane')}
                  onClick={() => onEditBot(bot.id)}
                >
                  <span class="map-select-item-name">{bot.label}</span>
                  <span class="start-choice-summary">{botSummary(bot.profile)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  if (view === 'scores') {
    const ranked = maps.filter((map) => map.topScores.length > 0);

    return (
      <div class="start-pane" role="region" aria-label="Top lists">
        {ranked.length === 0 ? (
          <p class="start-pane-empty">No finishes yet.</p>
        ) : (
          <div class="start-score-list">
            {ranked.map((map) => (
              <section
                key={map.id}
                class="start-score-block"
                data-map-scores={map.id}
              >
                <h3 class="map-select-item-name">{map.label}</h3>
                <MapTimes map={map} onReplay={onReplay} />
              </section>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div class="start-pane" role="region" aria-label="About">
      <p class="start-about-lead">Helmway</p>
      <p class="start-about-copy">
        A turn-based boat racing game. Each move is a heading and a speed: the
        next turn is drawn from the last.
      </p>
      <p class="start-about-copy">
        Pre-alpha. Maps and the movement loop are in. Hot-seat racing is a
        prototype. Online multiplayer and weapons are not.
      </p>
    </div>
  );
}

export function MapSelectPanel({
  maps,
  boats,
  bots = [],
  onSelect,
  onEdit,
  onBlankEditor,
  onEditBoat,
  onNewBoat,
  onEditBot,
  onNewBot,
  initialView,
  onReplay,
}: MapSelectProps): JSX.Element {
  const [view, setView] = useState<StartView>(initialView ?? 'idle');
  const [idleNav, setIdleNav] = useState<Exclude<StartView, 'idle'>>('race');
  const [raceMode, setRaceMode] = useState<RaceMode | null>(null);
  const [hotSeatBoats, setHotSeatBoats] = useState<number | null>(null);
  const [movement, setMovement] = useState<MatchMovement | null>(null);
  const [boatCollisions, setBoatCollisions] = useState<boolean | null>(null);
  const [raceMapId, setRaceMapId] = useState<string | null>(null);
  const [seatIndex, setSeatIndex] = useState(0);
  const [selectedBoatId, setSelectedBoatId] = useState<string | null>(null);
  const [helmLocked, setHelmLocked] = useState(true);
  const [picks, setPicks] = useState<RaceBoatPick[]>([]);
  const [names, setNames] = useState<string[]>([]);
  const [controllers, setControllers] = useState<SeatController[]>([]);
  const namesRef = useRef(names);
  namesRef.current = names;
  const boatChoices = useMemo(() => selectableBoats(boats), [boats]);
  const helmProfiles = useMemo(
    () => bots.map((entry) => ({ id: entry.id, label: entry.label, knobs: entry.profile.knobs })),
    [bots],
  );
  const rootRef = useRef<HTMLDivElement>(null);
  const armedRef = useRef(true);
  const pendingFocusRef = useRef<string | 'pane' | null>(null);
  const lastMapRef = useRef<string | null>(null);
  const viewRef = useRef(view);
  viewRef.current = view;
  const prevViewRef = useRef<StartView | null>(null);

  const mainCurrent = (itemId: Exclude<StartView, 'idle'>): boolean => {
    if (itemId === 'race') {
      // Default on load / idle focus; locked only while choosing Time Trial / Hot Seat.
      return (
        (view === 'idle' && idleNav === 'race') ||
        (view === 'race' && raceMode === null)
      );
    }

    if (view === 'idle') {
      return idleNav === itemId;
    }

    return view === itemId;
  };

  const clearBoatStep = (): void => {
    setRaceMapId(null);
    setSeatIndex(0);
    setSelectedBoatId(null);
    setHelmLocked(true);
    setPicks([]);
    setNames([]);
    setControllers([]);
  };

  const overlayEntryFocus = (hotSeat: boolean): string => {
    if (hotSeat) {
      return 'helm-human';
    }

    const mid = middleBoatId(boatChoices);
    return mid ? `hull-${mid}` : 'racer-name';
  };

  const pickRaceMode = (mode: RaceMode | null): void => {
    setRaceMode(mode);
    setHotSeatBoats(null);
    setMovement(null);
    setBoatCollisions(null);
    clearBoatStep();
  };

  const pickHotSeatCount = (count: number): void => {
    if (count === hotSeatBoats) {
      return;
    }

    setHotSeatBoats(count);

    // One boat has nobody to lock in against, and nobody to hit.
    // Clear any prior Movement / Collisions lock so the rows start unmarked.
    setMovement(null);
    setBoatCollisions(null);

    clearBoatStep();
  };

  const pickMovement = (next: MatchMovement): void => {
    setMovement(next);
  };

  const pickBoatCollisions = (next: boolean): void => {
    setBoatCollisions(next);
  };

  const pickMap = (id: string): void => {
    const count = raceMode === 'hotSeat' ? (hotSeatBoats ?? 1) : 1;
    const hotSeat = raceMode === 'hotSeat';
    lastMapRef.current = id;
    setRaceMapId(id);
    setSeatIndex(0);
    setSelectedBoatId(hotSeat ? null : middleBoatId(boatChoices));
    setHelmLocked(!hotSeat);
    setPicks([]);
    setNames(dealCompetitorNames(count));
    setControllers(Array.from({ length: count }, (): SeatController => 'human'));
    if (armedRef.current) {
      pendingFocusRef.current = overlayEntryFocus(hotSeat);
    }
  };

  const confirmBoat = (choice: BoatSelectChoice): void => {
    if (!raceMapId) {
      return;
    }

    const count = raceMode === 'hotSeat' ? (hotSeatBoats ?? 1) : 1;
    const next = [...picks];
    next[seatIndex] = {
      id: choice.id,
      boat: choice.boat,
      name: namesRef.current[seatIndex] ?? '',
      controller: raceMode === 'hotSeat' ? (controllers[seatIndex] ?? 'human') : 'human',
    };

    if (seatIndex + 1 < count) {
      setPicks(next);
      setSeatIndex(seatIndex + 1);
      setSelectedBoatId(null);
      setHelmLocked(false);
      if (armedRef.current) {
        pendingFocusRef.current = 'helm-human';
      }
      return;
    }

    onSelect(
      raceMapId,
      count,
      next,
      count > 1 ? (movement ?? 'sequential') : 'sequential',
      count > 1 ? (boatCollisions ?? true) : true,
    );
  };

  const retreatBoat = (): void => {
    if (seatIndex > 0) {
      const previous = seatIndex - 1;
      setSeatIndex(previous);
      setSelectedBoatId(selectedIdForPick(picks[previous], boatChoices));
      setHelmLocked(true);
      if (armedRef.current) {
        const mid = selectedIdForPick(picks[previous], boatChoices);
        pendingFocusRef.current = mid ? `hull-${mid}` : 'helm-human';
      }
      return;
    }

    // Unlock helm before leaving the overlay, or step out when already unlocked.
    if (raceMode === 'hotSeat' && helmLocked) {
      setHelmLocked(false);
      setSelectedBoatId(null);
      if (armedRef.current) {
        pendingFocusRef.current = 'helm-human';
      }
      return;
    }

    clearBoatStep();
    if (armedRef.current) {
      pendingFocusRef.current = lastMapRef.current
        ? `map-${lastMapRef.current}`
        : 'pane';
    }
  };

  const stepBack = (): void => {
    if (view === 'race' && raceMapId !== null) {
      retreatBoat();
      return;
    }

    if (view === 'race' && raceMode === 'hotSeat' && hotSeatBoats !== null) {
      setHotSeatBoats(null);
      if (armedRef.current) {
        pendingFocusRef.current = 'hotSeat';
      }
      return;
    }

    if (view === 'race' && raceMode !== null) {
      pickRaceMode(null);
      if (armedRef.current) {
        pendingFocusRef.current = 'timeTrial';
      }
      return;
    }

    if (view !== 'idle') {
      setView('idle');
      if (armedRef.current) {
        pendingFocusRef.current = 'race';
      }
    }
  };

  const focusNavId = (id: string): void => {
    const root = rootRef.current;
    const el = root ? startNavElement(root, id) : null;
    if (el) {
      focusStartNav(el);
    }
  };

  const resolvePendingFocus = (want: string): string | null => {
    const root = rootRef.current;
    if (!root) {
      return null;
    }

    const layout = readStartNav(root);

    if (want === 'pane') {
      const group = layout.groups.find((entry) => entry.id !== 'main');
      return group?.items[0] ?? 'race';
    }

    if (want === 'maps') {
      return layout.groups.find((entry) => entry.id === 'maps')?.items[0] ?? null;
    }

    return want;
  };

  /** Prefer a layout pass when the DOM may grow; fall back if state did not change. */
  const queueFocus = (want: string): void => {
    pendingFocusRef.current = want;
    queueMicrotask(() => {
      if (pendingFocusRef.current !== want) {
        return;
      }

      const id = resolvePendingFocus(want);
      if (!id || !startNavElement(rootRef.current!, id)) {
        return;
      }

      pendingFocusRef.current = null;
      focusNavId(id);
    });
  };

  const lockHelmAndOpenBoats = (): void => {
    const mid = middleBoatId(boatChoices);
    setHelmLocked(true);
    if (mid) {
      setSelectedBoatId(mid);
      queueFocus(`hull-${mid}`);
      return;
    }

    queueFocus('racer-name');
  };

  useLayoutEffect(() => {
    const want = pendingFocusRef.current;
    if (!want) {
      return;
    }

    const id = resolvePendingFocus(want);
    if (!id) {
      return;
    }

    const el = rootRef.current ? startNavElement(rootRef.current, id) : null;
    if (!el) {
      return;
    }

    pendingFocusRef.current = null;
    focusStartNav(el);
  }, [
    view,
    raceMode,
    hotSeatBoats,
    raceMapId,
    seatIndex,
    movement,
    boatCollisions,
    helmLocked,
    selectedBoatId,
  ]);

  // Default highlight on boot; keep keyboard armed so the first key moves from Go Race.
  useLayoutEffect(() => {
    focusNavId('race');
  }, []);

  // Opening Go Race focuses Time Trial without locking it.
  useLayoutEffect(() => {
    const openedRace =
      view === 'race' &&
      prevViewRef.current !== 'race' &&
      raceMode === null;
    prevViewRef.current = view;

    if (openedRace) {
      queueFocus('timeTrial');
    }
  }, [view, raceMode]);

  useEffect(() => {
    if (view !== 'race') {
      setRaceMode(null);
      setHotSeatBoats(null);
      setMovement(null);
      setBoatCollisions(null);
      setRaceMapId(null);
      setSeatIndex(0);
      setSelectedBoatId(null);
      setPicks([]);
      setNames([]);
      setControllers([]);
    }
  }, [view]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      const dir = startNavDir(event.key);
      const isEnter = event.key === 'Enter';
      const isEscape = event.key === 'Escape';

      if (!dir && !isEnter && !isEscape) {
        return;
      }

      if (isTypingInField()) {
        return;
      }

      if ((isEnter || isEscape) && event.repeat) {
        return;
      }

      const root = rootRef.current;
      if (!root) {
        return;
      }

      event.preventDefault();

      if (isEscape) {
        stepBack();
        return;
      }

      if (dir) {
        const layout = readStartNav(root);
        const current =
          focusedStartNavId(root) ?? layout.groups[0]?.items[0] ?? 'race';
        const result = moveStartNav(layout, current, dir);

        if (result.type === 'back') {
          stepBack();
          return;
        }

        if (result.type === 'forward') {
          const el = startNavElement(root, result.id);
          if (!el) {
            return;
          }

          const group = el.dataset.startNav ?? '';

          // Hot Seat: Right past the helm row locks Human / bot and opens the boats.
          if (group === 'helm' && !helmLocked) {
            lockHelmAndOpenBoats();
            return;
          }

          // Rule pills: Right past the row locks the focused choice and advances.
          if (RACE_RULE_GROUPS.has(group) && el.dataset.startNavId) {
            el.click();
            queueFocus(focusAfterRaceRule(group, el.dataset.startNavId));
            return;
          }

          if (el.dataset.startNav === 'main') {
            if (viewRef.current === el.dataset.startNavId) {
              queueFocus('race');
            } else if (el.dataset.startNavId === 'race') {
              queueFocus('timeTrial');
            } else {
              queueFocus('pane');
            }
          } else if (
            group === 'race-mode' &&
            el.dataset.startNavId === 'timeTrial' &&
            raceMode !== 'timeTrial'
          ) {
            queueFocus('maps');
          } else if (
            group === 'race-mode' &&
            el.dataset.startNavId === 'hotSeat' &&
            raceMode !== 'hotSeat'
          ) {
            queueFocus('boats-1');
          }

          el.click();
          return;
        }

        const el = startNavElement(root, result.id);
        if (!el) {
          return;
        }

        focusStartNav(el);
        if (result.activate) {
          el.click();
        }

        // Up onto the name starts editing (click / Up both edit).
        if (dir === 'up' && result.id === 'racer-name') {
          el.click();
        }

        return;
      }

      const active = document.activeElement;
      const focused =
        active instanceof HTMLElement && root.contains(active) ? active : null;
      const navId = focused?.dataset.startNavId;
      const group = focused?.dataset.startNav;
      const overlayOpen = raceMapId !== null && view === 'race';

      if (overlayOpen) {
        if (navId === 'racer-name') {
          focused?.click();
          return;
        }

        if (group === 'helm' && !helmLocked) {
          lockHelmAndOpenBoats();
          return;
        }

        if (group === 'cards' && navId) {
          const hullId = navId.slice('hull-'.length);
          if (hullId !== selectedBoatId) {
            focused?.click();
            return;
          }
        }

        const choice = boatChoices.find((entry) => entry.id === selectedBoatId);
        if (choice && helmLocked) {
          confirmBoat(choice);
          return;
        }
      }

      // Enter on boat count / Movement / Collisions locks the pill and steps on.
      if (group && navId && RACE_RULE_GROUPS.has(group)) {
        focused?.click();
        queueFocus(focusAfterRaceRule(group, navId));
        return;
      }

      if (group === 'race-mode' && navId === 'timeTrial') {
        const selecting = raceMode !== 'timeTrial';
        focused?.click();
        if (selecting) {
          queueFocus('maps');
        } else if (armedRef.current) {
          queueFocus('timeTrial');
        }
        return;
      }

      if (group === 'race-mode' && navId === 'hotSeat') {
        const selecting = raceMode !== 'hotSeat';
        focused?.click();
        if (selecting) {
          queueFocus('boats-1');
        } else if (armedRef.current) {
          queueFocus('hotSeat');
        }
        return;
      }

      if (focused?.dataset.startNav === 'main') {
        if (viewRef.current === focused.dataset.startNavId) {
          queueFocus('race');
        } else if (focused.dataset.startNavId === 'race') {
          queueFocus('timeTrial');
        } else {
          queueFocus('pane');
        }
      }

      focused?.click();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    view,
    raceMode,
    hotSeatBoats,
    raceMapId,
    seatIndex,
    picks,
    names,
    controllers,
    boatChoices,
    selectedBoatId,
    helmLocked,
  ]);

  return (
    <div class="map-select-backdrop ui-chrome" ref={rootRef}>
      <div class="start-shell">
        <div class="map-select-lockup" aria-hidden="true">
          <img src="/assets/helmway-wordmark_text_only.png" alt="" />
        </div>
        <div class="start-body">
          <div class="start-nav-col">
            <nav class="start-menu" aria-label="Main menu">
              {MENU.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  class="start-menu-item"
                  data-start-item={item.id}
                  aria-current={mainCurrent(item.id) ? 'true' : undefined}
                  {...startNavProps('main', item.id, 'main')}
                  onFocus={() => {
                    if (view === 'idle') {
                      setIdleNav(item.id);
                    }
                  }}
                  onClick={() => {
                    setView((open) => {
                      if (open === item.id) {
                        setIdleNav(item.id);
                        return 'idle';
                      }

                      return item.id;
                    });
                  }}
                >
                  {item.label}
                </button>
              ))}
            </nav>
            <p class="start-version">Pre Alpha demo v.0.0.0.1</p>
          </div>
          {view !== 'idle' ? (
            <StartPane
              view={view}
              maps={maps}
              boats={boats}
              raceMode={raceMode}
              hotSeatBoats={hotSeatBoats}
              movement={movement}
              boatCollisions={boatCollisions}
              onRaceMode={pickRaceMode}
              onHotSeatBoats={pickHotSeatCount}
              onMovement={pickMovement}
              onBoatCollisions={pickBoatCollisions}
              onPickMap={pickMap}
              onEdit={onEdit}
              onBlankEditor={onBlankEditor}
              onEditBoat={onEditBoat}
              onNewBoat={onNewBoat}
              bots={bots}
              onEditBot={(id) => onEditBot?.(id)}
              onNewBot={() => onNewBot?.()}
              onReplay={onReplay}
            />
          ) : null}
        </div>
      </div>
      <aside class="start-credit">
        <p class="start-credit-kicker">A game by</p>
        <img src="/assets/top-logo.png" alt="13 pixlar" width="2172" height="724" />
      </aside>
      {view === 'race' && raceMapId !== null ? (
        <BoatSelect
          boats={boatChoices}
          helmProfiles={helmProfiles}
          selectedId={selectedBoatId}
          seatLabel={
            raceMode === 'hotSeat'
              ? `Player ${seatIndex + 1} of ${hotSeatBoats ?? 1}`
              : null
          }
          controller={raceMode === 'hotSeat' ? (controllers[seatIndex] ?? 'human') : null}
          helmLocked={helmLocked}
          onController={(next) =>
            setControllers((current) =>
              current.map((entry, index) => (index === seatIndex ? next : entry)),
            )
          }
          playerName={names[seatIndex] ?? ''}
          takenNames={names.filter((_, index) => index !== seatIndex)}
          onNameCommit={(name) => {
            const next = namesRef.current.map((entry, index) =>
              index === seatIndex ? name : entry,
            );
            namesRef.current = next;
            setNames(next);
          }}
          onSelect={setSelectedBoatId}
          onLockHelm={() => setHelmLocked(true)}
          onConfirm={confirmBoat}
          onDismiss={retreatBoat}
        />
      ) : null}
    </div>
  );
}

export function mountMapSelect(props: MapSelectProps): MapSelect {
  setPanel('mapSelect', props);

  return {
    destroy: () => setPanel('mapSelect', null),
  };
}
